/** Shared commercial catalog. Monetary amounts are integer USD cents; generation costs are credits. */
export const STYLE_CREDITS = { outline: 1, standard: 1.5, hatching: 2 } as const;

export type PlanId = "hobby" | "premium" | "studio";
export type BillingInterval = "monthly" | "yearly";
export const MIN_ARTISTS = 2;
export const MAX_ARTISTS = 20;
export const ADDITIONAL_ARTIST_CENTS = 3500;
export const YEARLY_DISCOUNT = 20;
export const PLAN_PRICES = {
  hobby: { monthlyCents: 1599, annualCents: 15348, credits: 20 },
  premium: { monthlyCents: 3499, annualCents: 33590, credits: 100 },
  studio: { monthlyCents: 7999, annualCents: 76790, credits: 120 },
} as const;
export function quotePlan(plan: PlanId, interval: BillingInterval, artists = MIN_ARTISTS) {
  const definition = PLAN_PRICES[plan];
  const seats = plan === "studio" ? Math.min(MAX_ARTISTS, Math.max(MIN_ARTISTS, Number.isInteger(artists) ? artists : MIN_ARTISTS)) : 1;
  const additionalArtists = plan === "studio" ? seats - MIN_ARTISTS : 0;
  const regularMonthlyCents = definition.monthlyCents + additionalArtists * ADDITIONAL_ARTIST_CENTS;
  const annualAdditionalArtistCents = ADDITIONAL_ARTIST_CENTS * 12 * (100 - YEARLY_DISCOUNT) / 100;
  const totalCents = interval === "yearly" ? definition.annualCents + additionalArtists * annualAdditionalArtistCents : regularMonthlyCents;
  const monthlyCents = interval === "yearly" ? Math.round(totalCents / 12) : regularMonthlyCents;
  return { regularMonthlyCents, monthlyCents, totalCents, credits: definition.credits * seats, seats, annualSavingCents: regularMonthlyCents * 12 - totalCents };
}
export type CreditPlan = Readonly<{ tier: PlanId; interval: BillingInterval; artists: number }>;
export function validSelection(value: unknown): value is CreditPlan {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const p = value as Partial<CreditPlan>;
  return ["hobby", "premium", "studio"].includes(p.tier ?? "") && ["monthly", "yearly"].includes(p.interval ?? "") && Number.isInteger(p.artists) && (p.tier === "studio" ? p.artists! >= MIN_ARTISTS && p.artists! <= MAX_ARTISTS : p.artists === 1);
}
export function offerKey(p: CreditPlan) { return `${p.tier}:${p.interval}:${p.artists}`; }
export function usdCents(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return null;
  const cents = Math.round(value * 100);
  return Number.isSafeInteger(cents) && Math.abs(value * 100 - cents) < 0.000001 ? cents : null;
}

/** Full-price replacement requires a higher monthly tier or more Studio seats. */
const PLAN_RANK: Record<PlanId, number> = { hobby: 0, premium: 1, studio: 2 };
export function canUpgradeMonthly(current: unknown, next: unknown): boolean {
  return validSelection(current) && validSelection(next) && current.interval === "monthly" && next.interval === "monthly"
    && (PLAN_RANK[next.tier] > PLAN_RANK[current.tier] || (current.tier === "studio" && next.tier === "studio" && next.artists > current.artists));
}
